<template>
  <v-card flat class="pa-0 record-form">
    <div v-if="readonly" class="clinical-readonly">
      <div class="clinical-readonly__grid">
        <div
          v-for="field in readonlyFields"
          :key="field.key"
          v-show="hasReadonlyValue(field.value)"
          class="clinical-readonly__item"
        >
          <p class="clinical-readonly__label">{{ field.label }}</p>
          <p class="clinical-readonly__value">{{ field.value }}</p>
        </div>
      </div>
      <p v-if="!readonlyFields.some(field => hasReadonlyValue(field.value))" class="caption grey--text ma-0">—</p>
    </div>
    <v-row v-else no-gutters>
      <v-col cols="12">
        <v-text-field
          v-model="localModel.name"
          outlined
          dense
          :label="_getLabel('LAB_EXAM_NAME')"
        ></v-text-field>
      </v-col>
      <v-col cols="12">
        <v-select
          v-model="localModel.type"
          :items="typeOptions"
          item-text="text"
          item-value="value"
          outlined
          dense
          :label="_getLabel('LAB_EXAM_TYPE')"
        ></v-select>
      </v-col>
      <v-col cols="12" sm="6" class="pr-sm-2">
        <v-text-field
          v-model="requestDateFormatted"
          outlined
          dense
          :label="_getLabel('REQUEST_DATE')"
          type="date"
          :max="todayDate"
        ></v-text-field>
      </v-col>
      <v-col cols="12" sm="6" class="pl-sm-2">
        <v-text-field
          v-model="resultDateFormatted"
          outlined
          dense
          :label="_getLabel('RESULT_DATE')"
          type="date"
          :max="todayDate"
        ></v-text-field>
      </v-col>
      <v-col cols="12">
        <v-textarea
          v-model="localModel.results"
          outlined
          dense
          :label="_getLabel('RESULTS')"
          rows="3"
        ></v-textarea>
      </v-col>
      <v-col cols="12">
        <v-textarea
          v-model="localModel.notes"
          outlined
          dense
          :label="_getLabel('NOTES')"
          rows="2"
        ></v-textarea>
      </v-col>
    </v-row>
  </v-card>
</template>

<script>
import labExamsEnum from "@/constants/lab-exams.enum.js";
import {
  timestampToDateInput,
  dateInputToTimestamp,
  dateInputToClinicalTimestamp,
} from "@/constants/records.utils.js";

export default {
  name: "LabExamForm",
  props: {
    value: {
      type: Object,
      default: () => ({}),
      required: false,
    },
    readonly: { type: Boolean, default: false },
  },
  data() {
    return {
      localModel: { ...this.value },
    };
  },
  computed: {
    todayDate() {
      return timestampToDateInput(Date.now());
    },
    typeOptions() {
      return labExamsEnum.getTypeList(this._getLabel);
    },
    readonlyFields() {
      const type = Number.isInteger(this.localModel.type)
        ? labExamsEnum.getTypeLabel(this.localModel.type, this._getLabel)
        : "";
      return [
        ['name', 'LAB_EXAM_NAME', this.localModel.name],
        ['type', 'LAB_EXAM_TYPE', type],
        ['requestDate', 'REQUEST_DATE', this.formatDate(this.localModel.requestDate)],
        ['resultDate', 'RESULT_DATE', this.formatDate(this.localModel.resultDate)],
        ['results', 'RESULTS', this.localModel.results],
        ['notes', 'NOTES', this.localModel.notes],
      ].map(([key, label, value]) => ({ key, label: this._getLabel(label), value }));
    },
    /**
     * Data clínica validada pelo backend: nunca pode ficar no futuro.
     */
    requestDateFormatted: {
      get() {
        if (!this.localModel.requestDate) return "";
        return timestampToDateInput(this.localModel.requestDate);
      },
      set(val) {
        this.localModel.requestDate = dateInputToClinicalTimestamp(val);
      },
    },
    resultDateFormatted: {
      get() {
        if (!this.localModel.resultDate) return "";
        return timestampToDateInput(this.localModel.resultDate);
      },
      set(val) {
        this.localModel.resultDate = dateInputToTimestamp(val) || undefined;
      },
    },
  },
  methods: {
    formatDate(value) {
      return Number.isFinite(value) ? this._getDateFromTimestamp(value) : "";
    },
    hasReadonlyValue(value) {
      return value !== null && value !== undefined && String(value).trim() !== "";
    },
  },
  watch: {
    value: {
      handler(val) {
        if (JSON.stringify(val) !== JSON.stringify(this.localModel)) {
          this.localModel = { ...(val || {}) };
        }
      },
      deep: true,
    },
    localModel: {
      handler(val) {
        if (JSON.stringify(val) !== JSON.stringify(this.value)) {
          this.$emit("input", val);
        }
      },
      deep: true,
    },
  },
};
</script>
<style scoped>
.record-form .v-input { margin-bottom: 8px; }
.clinical-readonly__grid { display: grid; gap: 8px; grid-template-columns: repeat(2, minmax(0, 1fr)); }
.clinical-readonly__item { background: #f5f6fa; border-radius: 8px; min-width: 0; padding: 10px 12px; }
.clinical-readonly__label { color: #667085; font-size: 11px; font-weight: 700; margin: 0 0 4px; }
.clinical-readonly__value { color: #193153; font-size: 14px; line-height: 1.45; margin: 0; overflow-wrap: anywhere; white-space: pre-wrap; }
@media (max-width: 599px) { .clinical-readonly__grid { grid-template-columns: 1fr; } }
</style>
